import { FileText, Users, MapPin, Star, TrendingUp } from 'lucide-react';
import { STATS } from '../data';
import { useLocation } from '../hooks/useLocation';

const iconMap: Record<string, { icon: React.ReactNode; gradient: string; color: string }> = {
  'Listings': {
    icon: <FileText className="w-6 h-6" />,
    gradient: 'from-primary-400 to-primary-600',
    color: 'text-primary-400'
  },
  'Users': {
    icon: <Users className="w-6 h-6" />,
    gradient: 'from-accent to-accent-dark',
    color: 'text-accent'
  },
  'Cities': {
    icon: <MapPin className="w-6 h-6" />,
    gradient: 'from-cyan-400 to-cyan-600',
    color: 'text-cyan-400'
  },
  'User Rating': {
    icon: <Star className="w-6 h-6" />,
    gradient: 'from-warning to-orange-500',
    color: 'text-warning'
  },
};

export default function StatsStrip(): JSX.Element {
  const { country, isLoading } = useLocation();

  return (
    <div
      className="stats-strip relative overflow-hidden"
      role="region"
      aria-label="Platform statistics"
    >
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-900 to-dark-950" />
      
      {/* Animated blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl animate-blob"
          style={{ animationDelay: '0s' }}
        />
        <div 
          className="absolute top-1/2 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-blob"
          style={{ animationDelay: '2s' }}
        />
      </div>
      
      {/* Top border gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Section header */}
        <div className="text-center mb-10 fade-up">
          <div className="inline-flex items-center gap-2 text-primary-400 text-sm font-semibold mb-2">
            <TrendingUp className="w-4 h-4" />
            <span>Platform Stats</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Connecting buyers and sellers across{' '}
            <span className="bg-gradient-to-r from-primary-400 to-accent bg-clip-text text-transparent">
              {isLoading 
                ? 'Pakistan' 
                : country 
                  ? country 
                  : 'Pakistan'
              }
            </span>
          </h2>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((stat, index) => {
            const config = iconMap[stat.label];
            return (
              <div 
                key={stat.label} 
                className="stat-item text-center group fade-up"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                {/* Icon with gradient background */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} shadow-lg shadow-primary-500/20 mb-4 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}>
                  <span className="text-white">{config.icon}</span>
                </div>
                
                {/* Number with count-up animation feel */}
                <div className="relative">
                  <div className="stats-number text-4xl font-bold text-white mb-1 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary-400 group-hover:to-accent transition-all duration-300">
                    {stat.number}
                  </div>
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 bg-gradient-to-r from-primary-500 to-accent" />
                </div>
                
                <div className="text-dark-400 text-sm font-medium group-hover:text-white/80 transition-colors">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bottom border gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent" />
    </div>
  );
}
